/**
 * svg-path-guard
 * ----------------------------------------------------------------------------
 * Global, one-time guard that prevents the recurring browser console error:
 *
 *     Error: <path> attribute d: Expected moveto path command ('M' or 'm'), "Z".
 *
 * ROOT CAUSE
 * Recharts 2.x builds an SVG <path d="..."> for every chart series. When a chart
 * receives an empty / degenerate / NaN data array (e.g. an ai-ia-pro page whose
 * query resolved to `[]`), its internal path generators (Polygon for <Radar>,
 * d3 `curveLinearClosed` / `curveMonotone` for <Line>/<Area>) can emit a `d`
 * string that does NOT start with a `moveto` command — most commonly a bare
 * `"Z"` (close-only) or a string that begins with `L`. The browser's SVG parser
 * rejects this and prints the error directly to the console. Because that error
 * originates in the native SVG parser (not via `console.error`), it cannot be
 * silenced by overriding `console.error` — it must be prevented at the source by
 * never letting a malformed `d` reach the DOM.
 *
 * FIX
 * Wrap `SVGElement.prototype.setAttribute` (and the `d` property setter where
 * available) so that any `d` value that is not a valid path — i.e. does not begin
 * with a `moveto` command (`M`/`m`) — is normalised to an empty string. An empty
 * `d` renders nothing (the intended behaviour for an empty chart) and never
 * triggers the parser error. Valid paths are passed through untouched, so chart
 * visuals are unaffected.
 *
 * This is installed once at app bootstrap (see main.tsx) and is a no-op on the
 * server / in environments without SVGElement.
 */

const MOVE_TO_RE = /^[Mm]/

/**
 * Returns a safe `d` value: the original if it is a valid path (starts with a
 * moveto command), otherwise an empty string. `null`/`undefined`/already-empty
 * values are returned as-is.
 */
export function safePathD(value: unknown): unknown {
  if (value == null) return value
  const str = String(value)
  if (str === '') return value
  // A valid path data string must begin with a moveto command ('M' or 'm').
  // Leading whitespace is tolerated by the spec, so trim before checking.
  const trimmed = str.trimStart()
  if (trimmed === '' || !MOVE_TO_RE.test(trimmed)) {
    return ''
  }
  return value
}

let installed = false

export function installSvgPathGuard(): void {
  if (installed) return
  if (typeof SVGElement === 'undefined') return
  installed = true

  // 1) Guard the generic attribute setter used by React for the `d` attribute.
  const proto = SVGElement.prototype as any
  const originalSetAttribute = proto.setAttribute
  if (typeof originalSetAttribute === 'function' && !originalSetAttribute.__svgPathGuard) {
    const patched = function (this: SVGElement, name: string, value: any) {
      if (name === 'd' && this instanceof SVGPathElement) {
        return originalSetAttribute.call(this, name, safePathD(value))
      }
      return originalSetAttribute.call(this, name, value)
    }
    ;(patched as any).__svgPathGuard = true
    proto.setAttribute = patched
  }

  // 2) Guard the `d` IDL property setter (some code / libs set element.d.baseVal
  //    or the reflected property directly). Best-effort — skipped if not defined.
  try {
    if (typeof SVGPathElement !== 'undefined') {
      const pathProto = SVGPathElement.prototype as any
      const desc =
        Object.getOwnPropertyDescriptor(pathProto, 'd') ||
        Object.getOwnPropertyDescriptor(SVGElement.prototype as any, 'd')
      // Only the reflected string property "pathLength"/"d" via setAttribute is
      // commonly used by React; the SVGAnimatedString `d` has no string setter,
      // so there is nothing extra to patch here. The setAttribute guard above is
      // the effective hook for React-rendered charts.
      void desc
    }
  } catch {
    /* no-op: property guard is best-effort */
  }
}
